import {
  Activity,
  Award,
  BarChart,
  Box,
  CheckCircle,
  Clock,
  Cog,
  DollarSign,
  Gauge,
  Layers,
  Send,
  Shield,
  Sliders,
  Star,
  Target,
  Trophy,
  Users,
  Wrench,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '../../../components/ui/button.js';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog.js';
import { Input } from '../../../components/ui/input.js';
import { Label } from '../../../components/ui/label.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select.js';
import { cn } from '../../../lib/utils.js';
import { KpiChip } from './KpiChip.js';
import type { KpiType } from '../types.js';

/**
 * Editor dialog for KPI types AND metrics. Both shapes share a
 * name/color/icon trio; metrics additionally carry an optional
 * `internalName` + `kpiTypeId` parent.
 *
 * Color + icon are constrained to small presets (10 + 13 options).
 * Legacy `wf-audit` supported freeform input via a ColorIconPicker
 * modal, but the legacy preset set is what every operator actually
 * used in production — saves a hover-pick widget for the parity port.
 */

export interface KpiEditorTypeValues {
  kind: 'type';
  name: string;
  color: string;
  icon: string;
}

export interface KpiEditorMetricValues {
  kind: 'metric';
  displayName: string;
  internalName: string;
  color: string;
  icon: string;
  kpiTypeId: number | null;
}

export type KpiEditorValues = KpiEditorTypeValues | KpiEditorMetricValues;

interface KpiEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial: KpiEditorValues;
  /** Required when editing a metric — the dropdown of parent types. */
  availableTypes?: ReadonlyArray<KpiType>;
  onSubmit: (values: KpiEditorValues) => Promise<void>;
  /** Title shown above the form; defaults derive from initial.kind +
   *  whether this is a create (no existing values) or an edit. */
  title?: string;
  submitting?: boolean;
  error?: Error | null;
}

const PALETTE: ReadonlyArray<{ value: string; label: string }> = [
  { value: '#f43f5e', label: 'Rose' },
  { value: '#f97316', label: 'Orange' },
  { value: '#facc15', label: 'Yellow' },
  { value: '#84cc16', label: 'Lime' },
  { value: '#22c55e', label: 'Green' },
  { value: '#14b8a6', label: 'Teal' },
  { value: '#06b6d4', label: 'Cyan' },
  { value: '#3b82f6', label: 'Blue' },
  { value: '#8b5cf6', label: 'Violet' },
  { value: '#ec4899', label: 'Pink' },
];

/**
 * Icon picker subset. All entries are keys that `KpiChip`'s
 * `ICON_MAP` already resolves — keeping the two in sync avoids the
 * "chip falls back to BarChart" trap when the editor offers a glyph
 * the chip can't render.
 */
const ICONS: ReadonlyArray<{ value: string; label: string; component: LucideIcon }> = [
  { value: 'dollar-sign', label: 'Dollar sign', component: DollarSign },
  { value: 'clock', label: 'Clock', component: Clock },
  { value: 'gauge', label: 'Gauge', component: Gauge },
  { value: 'bar-chart', label: 'Bar chart', component: BarChart },
  { value: 'activity', label: 'Activity', component: Activity },
  { value: 'target', label: 'Target', component: Target },
  { value: 'layers', label: 'Layers', component: Layers },
  { value: 'zap', label: 'Zap', component: Zap },
  { value: 'box', label: 'Box', component: Box },
  { value: 'sliders', label: 'Sliders', component: Sliders },
  { value: 'award', label: 'Award', component: Award },
  { value: 'check-circle', label: 'Check', component: CheckCircle },
  { value: 'cog', label: 'Cog', component: Cog },
  { value: 'send', label: 'Send', component: Send },
  { value: 'shield', label: 'Shield', component: Shield },
  { value: 'star', label: 'Star', component: Star },
  { value: 'trophy', label: 'Trophy', component: Trophy },
  { value: 'users', label: 'Users', component: Users },
  { value: 'wrench', label: 'Wrench', component: Wrench },
];

export function KpiEditorDialog({
  open,
  onOpenChange,
  initial,
  availableTypes = [],
  onSubmit,
  title,
  submitting,
  error,
}: KpiEditorDialogProps): JSX.Element {
  const [draft, setDraft] = useState<KpiEditorValues>(initial);
  useEffect(() => {
    if (open) setDraft(initial);
  }, [open, initial]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    await onSubmit(draft);
  };

  const isMetric = draft.kind === 'metric';
  const computedTitle = title ?? (draft.kind === 'type' ? 'KPI type' : 'KPI metric');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg border-foreground/15 bg-[color:var(--surface-overlay)] text-foreground">
        <DialogHeader>
          <DialogTitle className="text-foreground">{computedTitle}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {draft.kind === 'type' ? (
            <section>
              <Label className="mb-1 block text-sm text-foreground/80" htmlFor="kpi-editor-name">
                Name
              </Label>
              <Input
                id="kpi-editor-name"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="e.g. Efficiency"
                required
                autoFocus
                className="border-foreground/15 bg-foreground/[0.04] text-foreground placeholder:text-foreground/30"
              />
            </section>
          ) : (
            <>
              <section>
                <Label
                  className="mb-1 block text-sm text-foreground/80"
                  htmlFor="kpi-editor-display-name"
                >
                  Display name
                </Label>
                <Input
                  id="kpi-editor-display-name"
                  value={draft.displayName}
                  onChange={(e) => setDraft({ ...draft, displayName: e.target.value })}
                  placeholder="e.g. Hours saved"
                  required
                  autoFocus
                  className="border-foreground/15 bg-foreground/[0.04] text-foreground placeholder:text-foreground/30"
                />
              </section>
              <section>
                <Label
                  className="mb-1 block text-sm text-foreground/80"
                  htmlFor="kpi-editor-internal-name"
                >
                  Internal name
                </Label>
                <Input
                  id="kpi-editor-internal-name"
                  value={draft.internalName}
                  onChange={(e) =>
                    setDraft({ ...draft, internalName: e.target.value.replace(/\s+/g, '_') })
                  }
                  placeholder="e.g. hours_saved"
                  className="border-foreground/15 bg-foreground/[0.04] font-mono text-foreground placeholder:text-foreground/30"
                />
                <p className="mt-1 text-xs text-foreground/40">
                  Stable identifier used in code / blueprints. Spaces auto-convert to underscores.
                </p>
              </section>
              <section>
                <Label className="mb-1 block text-sm text-foreground/80">Parent KPI type</Label>
                <Select
                  value={draft.kpiTypeId === null ? '__none__' : String(draft.kpiTypeId)}
                  onValueChange={(v) =>
                    setDraft({
                      ...draft,
                      kpiTypeId: v === '__none__' ? null : Number(v),
                    })
                  }
                >
                  <SelectTrigger className="border-foreground/15 bg-foreground/[0.04] text-foreground">
                    <SelectValue placeholder="No parent type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No parent type</SelectItem>
                    {availableTypes.map((t) => (
                      <SelectItem key={t.id} value={String(t.id)}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </section>
            </>
          )}

          <section>
            <Label className="mb-1 block text-sm text-foreground/80">Color</Label>
            <div className="flex flex-wrap gap-1.5">
              {PALETTE.map((p) => {
                const active = draft.color === p.value;
                return (
                  <button
                    key={p.value}
                    type="button"
                    aria-label={p.label}
                    title={p.label}
                    onClick={() => setDraft({ ...draft, color: p.value })}
                    className={cn(
                      'h-7 w-7 rounded-full border-2 transition-all',
                      active
                        ? 'border-white scale-110'
                        : 'border-foreground/20 hover:border-foreground/60',
                    )}
                    style={{ backgroundColor: p.value }}
                  />
                );
              })}
            </div>
          </section>

          <section>
            <Label className="mb-1 block text-sm text-foreground/80">Icon</Label>
            <div className="grid grid-cols-7 gap-1.5">
              {ICONS.map(({ value, label, component: Icon }) => {
                const active = draft.icon === value;
                return (
                  <button
                    key={value}
                    type="button"
                    aria-label={label}
                    title={label}
                    onClick={() => setDraft({ ...draft, icon: value })}
                    className={cn(
                      'flex h-8 w-8 items-center justify-center rounded-md border transition-colors',
                      active
                        ? 'border-[color:var(--brand-yellow)] bg-foreground/10 text-foreground'
                        : 'border-foreground/15 bg-transparent text-foreground/60 hover:border-foreground/40 hover:text-foreground',
                    )}
                  >
                    <Icon className="h-4 w-4" />
                  </button>
                );
              })}
            </div>
          </section>

          <section className="flex items-center gap-3 rounded-md border border-foreground/10 bg-foreground/[0.02] p-3">
            <KpiChip icon={draft.icon} color={draft.color} size="lg" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-foreground">
                {isMetric ? draft.displayName || 'Preview' : draft.name || 'Preview'}
              </div>
              {isMetric && draft.internalName ? (
                <div className="truncate font-mono text-xs text-foreground/40">
                  {draft.internalName}
                </div>
              ) : null}
            </div>
          </section>

          {error ? (
            <div className="rounded-md border border-rose-400/30 bg-rose-500/10 p-2 text-xs text-rose-800 dark:text-rose-200">
              {error.message}
            </div>
          ) : null}

          <DialogFooter className="flex flex-row items-center justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-foreground/15 text-foreground/70"
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-[color:var(--brand-yellow)] text-[color:var(--gradient-end)] hover:opacity-90"
            >
              {submitting ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
